import React, { useState } from "react";
import { Plus, Trash2, X } from "lucide-react";

// ─── Categories (matches the charmy.net design) ───────────────────────────────
export const POST_PROCESS_CATEGORIES = [
  {
    category: "Whitespace",
    items: [
      { value: "trim",                desc: "Remove leading and trailing whitespace" },
      { value: "trimStart",           desc: "Remove leading whitespace only" },
      { value: "trimEnd",             desc: "Remove trailing whitespace only" },
      { value: "collapseSpaces",      desc: "Collapse multiple spaces into one" },
      { value: "normalizeLineBreaks", desc: "Normalize \\r\\n and \\r to \\n" },
    ],
  },
  {
    category: "Case",
    items: [
      { value: "lcase",     desc: "Convert to lowercase" },
      { value: "ucase",     desc: "Convert to UPPERCASE" },
      { value: "capitalize",desc: "Capitalize first letter of each word" },
      { value: "titleCase", desc: "Title case (lowercase small words)" },
    ],
  },
  {
    category: "Transform",
    items: [
      { value: "reverse", desc: "Reverse the string" },
      { value: "slug",    desc: "Convert to URL slug (lowercase, hyphens)" },
    ],
  },
  {
    category: "Cleaning",
    items: [
      { value: "removeSpaces",          desc: "Remove all whitespace characters" },
      { value: "removeDuplicates",      desc: "Remove duplicate characters" },
      { value: "removeNonAlpha",        desc: "Keep only letters (A-Z a-z)" },
      { value: "removeNonNumeric",      desc: "Keep only digits (0-9)" },
      { value: "removeNonAlphaNumeric", desc: "Keep only letters and digits" },
      { value: "stripHtml",             desc: "Strip HTML tags from text" },
    ],
  },
  {
    category: "Encoding",
    items: [
      { value: "normalizeUnicode", desc: "Normalize unicode characters (NFC)" },
    ],
  },
  {
    category: "Format",
    items: [
      { value: "formatDate",  desc: "Normalize common date formats to YYYY-MM-DD" },
      { value: "formatPhone", desc: "Normalize phone numbers (digits only)" },
      { value: "date_format", desc: "Reformat to a specific date pattern" },
    ],
  },
  {
    category: "Extract",
    items: [
      { value: "extractNumbers", desc: "Extract first numeric value" },
      { value: "extractEmails",  desc: "Extract first email address found" },
    ],
  },
  {
    category: "Advanced",
    items: [
      { value: "replace", desc: "Find & Replace with multiple regex rules" },
    ],
  },
];

export const ALL_PROC_TYPES = POST_PROCESS_CATEGORIES.flatMap(c =>
  c.items.map(i => ({ ...i, category: c.category }))
);

export const getDefaultParams = (type) => {
  if (type === "date_format") return { output_format: "YYYY-MM-DD" };
  if (type === "replace") return { rules: [{ find: "", replacement: "", flags: "g", is_regex: true }] };
  return {};
};

// ─── Core post-processing logic ───────────────────────────────────────────────
const _applyStep = (v, step) => {
  const p = step.params || {};
  switch (step.type) {
    case "trim":                return v.trim();
    case "trimStart":           return v.trimStart();
    case "trimEnd":             return v.trimEnd();
    case "collapseSpaces":      return v.replace(/\s+/g, " ");
    case "normalizeLineBreaks": return v.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    case "lcase": case "lowercase": return v.toLowerCase();
    case "ucase": case "uppercase": return v.toUpperCase();
    case "capitalize": return v.replace(/\b\w/g, c => c.toUpperCase());
    case "titleCase": {
      const small = new Set(["a","an","the","and","but","or","for","nor","on","at","to","in","by","of"]);
      return v.toLowerCase().split(" ").map((w, i) => (!i || !small.has(w)) ? w.charAt(0).toUpperCase() + w.slice(1) : w).join(" ");
    }
    case "reverse": return v.split("").reverse().join("");
    case "slug": return v.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    case "removeSpaces":          return v.replace(/\s/g, "");
    case "removeDuplicates":      return [...new Set(v)].join("");
    case "removeNonAlpha":        return v.replace(/[^a-zA-Z]/g, "");
    case "removeNonNumeric":      return v.replace(/[^0-9]/g, "");
    case "removeNonAlphaNumeric": return v.replace(/[^a-zA-Z0-9]/g, "");
    case "stripHtml":             return v.replace(/<[^>]*>/g, "");
    case "normalizeUnicode":      return v.normalize("NFC");
    case "formatDate": {
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    }
    case "formatPhone": return v.replace(/[^\d+]/g, "");
    case "extractNumbers": { const m = v.match(/[\d.]+/); return m ? m[0] : v; }
    case "extractEmails": {
      const m = v.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
      return m ? m[0] : v;
    }
    case "date_format": {
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const pad = n => String(n).padStart(2, "0");
      const ms = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      return (p.output_format || "YYYY-MM-DD")
        .replace("YYYY", d.getFullYear())
        .replace("MMM", ms[d.getMonth()])
        .replace("MM", pad(d.getMonth() + 1))
        .replace("DD", pad(d.getDate()));
    }
    case "replace": {
      const rules = p.rules || [];
      let result = v;
      for (const rule of rules) {
        if (!rule.find) continue;
        try {
          const pattern = rule.is_regex === false
            ? rule.find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")  // escape for literal
            : rule.find;
          result = result.replace(new RegExp(pattern, rule.flags || "g"), rule.replacement ?? "");
        } catch {}
      }
      return result;
    }
    default: return v;
  }
};

export const applyPostProcessing = (value, steps) => {
  if (!value || !steps?.length) return value;
  let v = String(value);
  for (const step of steps) {
    try { v = _applyStep(v, step); } catch {}
  }
  return v;
};

// ─── Add Step Dropdown (categorized) ─────────────────────────────────────────
export function AddStepDropdown({ onAdd, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 h-6 px-2 text-[11px] font-medium rounded border border-[#006080]/30 text-[#006080] hover:bg-[#E5F0F2] transition-colors"
      >
        <Plus size={10} /> Add Step
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-7 bg-white border border-slate-200 rounded-lg shadow-xl z-20 w-64 overflow-hidden">
            <div className="max-h-64 overflow-y-auto">
              {POST_PROCESS_CATEGORIES.map(cat => (
                <div key={cat.category}>
                  <div className="px-3 py-1.5 text-xs font-semibold text-teal-600 bg-gray-50/80 sticky top-0 border-b border-slate-100/60">
                    {cat.category}
                  </div>
                  {cat.items.map(item => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => { onAdd(item.value); setOpen(false); }}
                      className="w-full text-left px-3 py-2 text-xs transition-colors hover:bg-slate-50 text-slate-700"
                    >
                      <span className="font-mono font-semibold block">{item.value}</span>
                      <span className="text-slate-400 text-[11px] leading-tight">{item.desc}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Single Step Row ──────────────────────────────────────────────────────────
export function PostProcessStepRow({ step, onRemove, onUpdate }) {
  const typeInfo = ALL_PROC_TYPES.find(t => t.value === step.type);
  const rules = step.params?.rules || [];

  const addRule = () => onUpdate("rules", [...rules, { find: "", replacement: "", flags: "g", is_regex: true }]);
  const removeRule = (rIdx) => onUpdate("rules", rules.filter((_, i) => i !== rIdx));
  const updateRule = (rIdx, key, val) => {
    const updated = [...rules];
    updated[rIdx] = { ...updated[rIdx], [key]: val };
    onUpdate("rules", updated);
  };

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
      {/* Step header */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2 min-w-0">
          <code className="text-xs font-mono font-semibold text-slate-700">{step.type}</code>
          {step.type === "replace" && rules.length > 0 && (
            <span className="w-4 h-4 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold inline-flex items-center justify-center flex-shrink-0">
              {rules.length}
            </span>
          )}
          <span className="text-[11px] text-slate-400 truncate hidden sm:block">{typeInfo?.desc}</span>
        </div>
        <button type="button" onClick={onRemove} className="p-1 hover:bg-slate-200 rounded text-slate-400 ml-1 flex-shrink-0">
          <X size={11} />
        </button>
      </div>

      {/* date_format: output format selector */}
      {step.type === "date_format" && (
        <div className="px-3 py-2">
          <select
            value={step.params?.output_format || "YYYY-MM-DD"}
            onChange={e => onUpdate("output_format", e.target.value)}
            className="h-7 px-2 text-xs border border-slate-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#006080]"
          >
            {["YYYY-MM-DD","DD/MM/YYYY","MM/DD/YYYY","DD-MMM-YYYY","YYYY/MM/DD"].map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
      )}

      {/* replace: multi-rule editor */}
      {step.type === "replace" && (
        <div className="px-3 py-2.5 space-y-2.5">
          {/* Header row */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-[#006080]">
              Find &amp; Replace
              {rules.length > 0 && (
                <span className="ml-1.5 w-4 h-4 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold inline-flex items-center justify-center align-middle">
                  {rules.length}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={addRule}
              className="flex items-center gap-1 text-[11px] font-medium text-[#006080] hover:bg-[#E5F0F2] px-1.5 py-0.5 rounded transition-colors"
            >
              <Plus size={10} /> Add rule
            </button>
          </div>

          {rules.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No rules yet — click &quot;+ Add rule&quot;</p>
          ) : (
            <div className="space-y-2">
              {rules.map((rule, rIdx) => (
                <div key={rIdx} className="border border-slate-100 rounded-md p-2 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-slate-400 w-4 flex-shrink-0 text-right">{rIdx + 1}.</span>
                    <div className="flex-1 flex items-center gap-1.5 min-w-0">
                      <input
                        value={rule.find || ""}
                        onChange={e => updateRule(rIdx, "find", e.target.value)}
                        placeholder={rule.is_regex === false ? "Plain text..." : "Pattern (regex)..."}
                        className={`flex-1 h-7 px-2 text-xs font-mono border rounded focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white min-w-0 ${
                          rule.is_regex === false ? "border-slate-200" : "border-[#006080]/30"
                        }`}
                      />
                      <span className="text-xs text-slate-300 flex-shrink-0">→</span>
                      <input
                        value={rule.replacement ?? ""}
                        onChange={e => updateRule(rIdx, "replacement", e.target.value)}
                        placeholder="Replace with..."
                        className="flex-1 h-7 px-2 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white min-w-0"
                      />
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Regex/Text mode toggle */}
                      <button
                        type="button"
                        onClick={() => updateRule(rIdx, "is_regex", rule.is_regex === false ? true : false)}
                        title={rule.is_regex === false ? "Plain text mode — click to switch to regex" : "Regex mode — click to switch to plain text"}
                        className={`h-7 px-1.5 text-[11px] font-mono border rounded transition-colors flex-shrink-0 ${
                          rule.is_regex === false
                            ? "bg-slate-50 text-slate-400 border-slate-200"
                            : "bg-amber-50 text-amber-700 border-amber-300 font-semibold"
                        }`}
                      >
                        [.*]
                      </button>
                      {/* Flags badge — amber styled */}
                      <input
                        value={rule.flags ?? "g"}
                        onChange={e => updateRule(rIdx, "flags", e.target.value)}
                        title="Regex flags (g=global, i=case-insensitive, m=multiline)"
                        className="w-8 h-7 px-1 text-xs font-mono border border-amber-300 bg-amber-50 text-amber-700 rounded focus:outline-none focus:ring-1 focus:ring-amber-400 text-center"
                      />
                      <button
                        type="button"
                        onClick={() => removeRule(rIdx)}
                        className="p-1 hover:bg-red-50 rounded text-red-400"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
